import { useState } from 'react';
import { useInView } from '../../hooks/useInView';

interface FAQItem {
  question: string;
  answer: string;
}

interface SectionFAQProps {
  heading?: string;
  items: FAQItem[];
}

export default function SectionFAQ({ heading = 'Frequently Asked Questions', items }: SectionFAQProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { ref: headRef, isVisible: headVisible } = useInView();
  const { ref: listRef, isVisible: listVisible } = useInView({ threshold: 0.05 });

  return (
    <section className="bg-rdr-cream py-24 lg:py-32 overflow-hidden">
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div
          ref={headRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-16 fade-up ${headVisible ? 'is-visible' : ''}`}
        >
          <div className="inline-flex items-center gap-4 mb-5">
            <span className="gold-divider" />
            <p className="text-rdr-gold text-xs tracking-[0.25em] uppercase font-sans">FAQ</p>
            <span className="gold-divider" />
          </div>
          <h2 className="font-serif text-rdr-charcoal text-4xl lg:text-5xl xl:text-6xl leading-tight">
            {heading}
          </h2>
        </div>

        {/* FAQ Items */}
        <div
          ref={listRef as React.RefObject<HTMLDivElement>}
          className="space-y-0"
          itemScope
          itemType="https://schema.org/FAQPage"
        >
          {items.map((item, i) => (
            <div
              key={i}
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
              className={`border-b border-rdr-cream-dark fade-up ${listVisible ? 'is-visible' : ''}`}
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              <button
                className="w-full flex items-center justify-between py-7 text-left cursor-pointer group"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                aria-expanded={openIndex === i}
              >
                <span
                  itemProp="name"
                  className="font-serif text-rdr-charcoal text-lg lg:text-xl pr-10 group-hover:text-rdr-warm transition-colors duration-300 leading-snug"
                >
                  {item.question}
                </span>
                <div
                  className={`w-9 h-9 flex items-center justify-center flex-shrink-0 border rounded-full transition-all duration-300 ${
                    openIndex === i
                      ? 'border-rdr-gold bg-rdr-gold/10'
                      : 'border-rdr-charcoal/20 bg-transparent'
                  }`}
                >
                  <i
                    className={`${openIndex === i ? 'ri-subtract-line' : 'ri-add-line'} text-base ${
                      openIndex === i ? 'text-rdr-gold' : 'text-rdr-muted'
                    }`}
                  />
                </div>
              </button>
              <div
                itemScope
                itemProp="acceptedAnswer"
                itemType="https://schema.org/Answer"
                className={`overflow-hidden transition-all duration-400 ${
                  openIndex === i ? 'max-h-96 opacity-100 pb-7' : 'max-h-0 opacity-0'
                }`}
              >
                <p itemProp="text" className="text-rdr-muted font-sans text-base leading-relaxed pr-12">
                  {item.answer}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
